import { useEffect, useState } from "react";
import { ChevronDown, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { Field, NativeSelect } from "@/components/common";
import { JobField } from "./JobFields";
import { COVER_KEYS } from "./CoverSection";

const cache = {};
export const clearSchemaCache = () => Object.keys(cache).forEach((k) => delete cache[k]);
export function loadSchema(sl, code) {
  if (!sl || !code) return Promise.resolve(null);
  const k = `${sl}/${code}`;
  if (!cache[k]) cache[k] = api.get(`/estimating/job-types/${sl}/${code}/form-schema?finishing=*`).then((r) => r.data).catch(() => { delete cache[k]; return null; });
  return cache[k];
}
export function useSchema(sl, code) {
  const [s, setS] = useState(null);
  useEffect(() => { let on = true; setS(null); loadSchema(sl, code).then((x) => on && setS(x)); return () => { on = false; }; }, [sl, code]);
  return s;
}

export const isBlank = (v) => v === undefined || v === null || v === "" || v === false || (Array.isArray(v) && !v.length);
const finKeys = (s) => new Set((s?.finishing_options || []).flatMap((o) => [...o.toggle_fields, ...o.fields.map((f) => f.field_key)]));
const allKeys = (s) => new Set([...(s?.form_fields || []).map((f) => f.field_key), ...finKeys(s)]);

// Keep values for core fields the new type also has, clear the rest, reset finishing, then fill new defaults.
export function resetJobValues(values, oldS, newS) {
  const keep = new Set((newS?.form_fields || []).map((f) => f.field_key));
  const touched = new Set([...allKeys(oldS), ...finKeys(newS)]);
  const out = Object.fromEntries(Object.entries(values).filter(([k, v]) => !touched.has(k) || (keep.has(k) && !isBlank(v))));
  (newS?.form_fields || []).forEach((f) => { if (isBlank(out[f.field_key]) && f.default_value != null) out[f.field_key] = f.default_value; });
  return { ...out, finishing_addons: [] };
}

const shown = (f, l, keys) => {
  const s = f.show_if;
  if (!s) return true;
  if (s.includes("=")) { const [k, v] = s.split("="); return String(l[k] ?? "") === v; }
  return !(keys.has(s) && !l[s]);
};
export const requiredMissing = (s, l) => {
  const keys = allKeys(s);
  const sized = Number(l.width_mm) > 0 && Number(l.length_mm) > 0;  // finished_size is captured by the core Finish size picker
  return (s?.form_fields || []).filter((f) => f.visibility === "required" && f.field_key !== "quantity_unit" && !(f.field_key === "finished_size" && sized) && shown(f, l, keys) && isBlank(l[f.field_key]));
};

function SchemaField({ f, l, set, subs, sub, variants, tid, hl, pagesSummary }) {
  const gsmOptions = sub?.gsm_options?.length ? sub.gsm_options : sub?.gsm ? [sub.gsm] : [];
  if ((f.type === "gsm" && !gsmOptions.length) || (f.type === "variant" && !variants.length)) return null;
  const label = `${f.label}${f.unit && !f.label.includes(f.unit) ? ` (${f.unit})` : ""}${f.visibility === "required" ? " *" : ""}`;
  const def = { label, type: f.type === "checkbox" ? "bool" : f.type, options: f.options, gsmOptions, variants };
  return <div className={hl && f.visibility === "required" && isBlank(l[f.field_key]) ? "rounded-md ring-2 ring-amber-300 ring-offset-2" : f.field_key === "page_count" ? "" : "contents"} data-testid={tid(`field-${f.field_key}`)}>
    <JobField k={f.field_key} def={def} l={l} set={set} subs={subs} tid={tid} />
    {f.field_key === "page_count" && pagesSummary && <p className="mt-1 text-xs text-slate-600" data-testid={tid("pages-summary")}>{pagesSummary}</p>}</div>;
}

function FinishingPicker({ s, l, patch, ctx }) {
  const sel = l.finishing_addons || [];
  const toggle = (o) => {
    const on = sel.includes(o.key);
    const vals = on ? Object.fromEntries([...o.toggle_fields, ...o.fields.map((f) => f.field_key)].map((k) => [k, undefined]))
      : { ...Object.fromEntries(o.toggle_fields.map((k) => [k, true])), ...Object.fromEntries(o.fields.filter((f) => f.default_value != null && isBlank(l[f.field_key])).map((f) => [f.field_key, f.default_value])) };
    patch({ ...vals, ...(!on && ctx.onFinishingAdd ? ctx.onFinishingAdd(o) : {}), finishing_addons: on ? sel.filter((x) => x !== o.key) : [...sel, o.key] });
  };
  return <div className="space-y-2" data-testid={ctx.tid("finishing-list")}>{s.finishing_options.map((o) => (
    <div key={o.key} className="rounded-md border border-slate-200 p-2">
      <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" className="h-4 w-4 accent-[#13294B]" data-testid={ctx.tid(`finishing-opt-${o.key}`)} checked={sel.includes(o.key)} onChange={() => toggle(o)} />{o.label}{o.price_hint && <span className="text-xs text-slate-500" data-testid={ctx.tid(`finishing-hint-${o.key}`)}>— {o.price_hint}</span>}
        {o.linked_operations?.length > 0 && <span className="text-[11px] text-slate-400" data-testid={ctx.tid(`finishing-ops-${o.key}`)}>· costed: {o.linked_operations.map((x) => x.name).join(", ")}</span>}</label>
      {sel.includes(o.key) && o.fields.length > 0 && <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid={ctx.tid(`finishing-fields-${o.key}`)}>
        {o.fields.filter((f) => shown(f, l, allKeys(s))).map((f) => <SchemaField key={f.field_key} f={f} l={l} {...ctx} />)}</div>}
    </div>))}</div>;
}

export function ProgressiveFields({ l, patch, slLabel, subs, sub, variants = [], tid, highlight, finishSignal, full = "sm:col-span-2 lg:col-span-4", omit = [], pagesSummary, onFinishingAdd }) {
  const s = useSchema(l.service_line, l.job_type);
  const [optOpen, setOptOpen] = useState(false);
  const [finOpen, setFinOpen] = useState(false);
  useEffect(() => { setFinOpen(false); setOptOpen(false); }, [l.service_line, l.job_type]);
  useEffect(() => { if (finishSignal) setFinOpen(true); }, [finishSignal]);
  useEffect(() => {
    if (!s || l.finishing_addons) return;
    const used = s.finishing_options.filter((o) => [...o.toggle_fields, ...o.fields.map((f) => f.field_key)].some((k) => !isBlank(l[k]))).map((o) => o.key);
    patch({ finishing_addons: used });
  }, [s]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!s) return null;
  const keys = allKeys(s);
  const set = (k, v) => patch({ [k]: v });
  const ctx = { set, subs, sub, variants, tid, hl: highlight, pagesSummary, onFinishingAdd };
  const coverOwn = s.form_fields.some((f) => f.field_key === "separate_cover");
  const vis = (v) => s.form_fields.filter((f) => f.visibility === v && f.field_key !== "quantity_unit" && !omit.includes(f.field_key) && !(coverOwn && COVER_KEYS.includes(f.field_key)) && shown(f, l, keys));
  const req = vis("required"), opt = vis("optional");
  const ready = !requiredMissing(s, l).length;
  return <>
    <div className={`${full} flex flex-wrap items-center justify-between gap-2`}>
      <p className="text-xs text-slate-500" data-testid={tid("field-summary")}>Showing {req.length} required field{req.length === 1 ? "" : "s"} for: <b className="text-slate-700">{slLabel} → {s.label}</b></p>
      {s.templates.length > 0 && <div className="w-56"><NativeSelect testId={tid("template")} value="" placeholder="Apply a template" onChange={(n) => { const t = s.templates.find((x) => x.name === n); if (t) patch(t.values); }} options={s.templates.map((t) => ({ value: t.name, label: t.name }))} /></div>}
    </div>
    {req.map((f) => <SchemaField key={f.field_key} f={f} l={l} {...ctx} />)}
    {s.finishing_options.length > 0 && (ready || finOpen || (l.finishing_addons || []).length > 0) && <div className={full}>
      {!finOpen ? <button type="button" data-testid={tid("add-finishing")} onClick={() => setFinOpen(true)} className="flex items-center gap-1 rounded-full border border-[#13294B] px-3 py-1 text-xs font-medium text-[#13294B] transition-colors hover:bg-[#13294B] hover:text-white"><Plus className="h-3.5 w-3.5" />Add finishing</button>
        : <Field label="Finishing"><FinishingPicker s={s} l={l} patch={patch} ctx={ctx} /></Field>}
    </div>}
    {opt.length > 0 && <div className={full}>
      <button type="button" data-testid={tid("optional-toggle")} onClick={() => setOptOpen(!optOpen)} className="flex items-center gap-1 text-sm font-medium text-[#389F95]">
        <ChevronDown className={`h-4 w-4 transition-transform ${optOpen ? "rotate-180" : ""}`} />Optional / Advanced ({opt.length})</button>
      {optOpen && <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid={tid("optional-section")}>{opt.map((f) => <SchemaField key={f.field_key} f={f} l={l} {...ctx} />)}</div>}
    </div>}
  </>;
}
